<template>
    <div class="message-area">
        <InformationOverlay
            :key="contact._id"
            :status="contactStatus"
            :contact="contact"
            @focusEditor="focusEditor"
            @blurEditor="blurEditor"
        />
        <MessageAreaHeader
            :mode="conversationMode"
            :superMenuState="superMenuState"
            @command="setMessage"
            @modeChanged="modeChanged"
            @updateSuperMenuState="updateSuperMenuState"
            @usedSlashCommand="usedSlashCommand"
            :contact="contact"
        />
        <div class="message-area-container">
            <MailSubject v-model="mailSubject" v-if="conversationMode === 'email'" />
            <SMSInput :value="smsInput" @input="(input) => (smsInput = input)" v-if="conversationMode === 'sms'" />
            <ChatEditor
                ref="editor"
                v-model="message"
                :file-send="!['sms', 'email'].includes(conversationMode)"
                :is-text="conversationMode === 'sms'"
                :enabled-banned-words="true"
                :emoji-picker="true"
                :speech-recognition="true"
                :share-location="true"
                :share-attachment="true"
                :placeholder="generateChatSuggestions()"
                @ai-tools="changeWithAI"
                @updateSuperMenuState="updateSuperMenuState"
                @submit="submitForm"
                @location="sendLocation"
                @voice-message="sendVoiceMessage"
                @media-message="$emit('media-message', $event)"
                :editable="!contactStatus"
            />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import LocationShare from "../../../../LocationShare.vue";
import SendIcon from "../../../../svg/SendIcon.vue";
import VoiceRecorder from "../../../../VoiceRecorder.vue";
import MicButton from "../../../../../utils/speech/MicButton.vue";
import CoPilotButton from "../../../../ui/radial-menu/CoPilotButton.vue";
import EmojiButton from "../../../../EmojiButton.vue";
import _ from "lodash";
import CKEditor from "@ckeditor/ckeditor5-vue";
import TiptapEditor from "../../../../../views/Pages/Chatbot/pages/dashboard/common/TiptapEditor.vue";
import ChatEditor from "./Editor.vue";
import RadialMenu from "../../../../ui/radial-menu/RadialMenu.vue";
import systemPubSub from "../../../../../utils/systemPubSub";
import { log2 } from "../../../../../utils/log2";
import { filter, first, map } from "rxjs/operators";
import { sendSocketMessage, socketMessageStream } from "../../../../../utils/socketStream";
import { generateUUID } from "../../../../../utils/session";
import MessageAreaHeader from "./MessageAreaHeader.vue";
import SMSInput from "./SMSInput.vue";
import MailSubject from "./MailSubject.vue";
import axios from "axios";
import { uploadAsset } from "../../../../../api/assetUpload";
import getRandomShortcut from "../../../../../utils/shortcutAll";
import InformationOverlay from "./InformationOverlay.vue";

const log = log2("MessageArea");
export default {
    name: "MessageArea",
    components: {
        InformationOverlay,
        MailSubject,
        SMSInput,
        MessageAreaHeader,
        RadialMenu,
        ChatEditor,
        TiptapEditor,
        CoPilotButton,
        MicButton,
        VoiceRecorder,
        SendIcon,
        LocationShare,
        EmojiButton,
        CKEditor,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        messageSyncRunning: {
            type: Boolean,
            default: false,
        },
        radialMenuOpened: {
            type: Boolean,
            default: false,
        },
        dragMode: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            smsChannels: (state) => state.channel.channels.filter((x) => x.type === "sms-postacell"),
            customerLocationEnabled: (state) => _.get(state, "setting.settings.customerLocationEnabled", true),
            agentFileSendEnabled: (state) => _.get(state, "setting.settings.agentFileSendEnabled", true),
            agentVoiceRecordEnabled: (state) => _.get(state, "setting.settings.agentVoiceRecordEnabled", true),
            allMailAddresses: (state) => state.mail.allMailAddresses,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            settings: (state) => state.setting.settings,
            draftMessages: (state) => state.chat.draftMessages,
            replyToMessages: (state) => state.chat.pinnedReplyToMessages,
            liveVisitors: (state) => state.visitor.liveVisitors,
            watchingContactIds: (state) => state.chat.watchingContactIds,
        }),
        ...mapGetters({
            isWhatsappHealthy: "whatsapp/isHealthy",
        }),
        contactStatus() {
            const isWebPlatform = this.contact.platform === "web";
            const isClosed = this.contact.lastConversation?.status === "destroyed";
            const isLiveVisitor = !!this.liveVisitors.includes(this.contact._id);
            const isAssignMe = this.contact.lastConversation?.users?.includes(this.user._id);

            if (isClosed) return "enter-create";
            else if (!isAssignMe) return "enter-join";
            // /**
            //  * Web Visitor Kuralları
            //  * 1. Eğer Kullanıcı Aktif ve Chat Destroy Olmuş ise Chat Başlatma Aktiftir.
            //  * 2. Eğer Kullanıcı Aktif, Chat Aktif ve Chat içinde Temsilci yoksa Chat Katıl Aktiftir.
            //  * 3. Eğer Kullanıcı Offline ve Chat Destroy Olmuş ise Inactive Chattir
            //  *
            //  * Other Platforms
            //  * 1. Eğer Chat Destroy ise Chat Başlatma Aktiftir.
            //  * 2. Eğer Chat Aktif ve Temsilci Yoksa Chat Katıl
            //  */
            //
            // if (isWebPlatform) {
            //     if (isLiveVisitor && isClosed) return "enter-create";
            //     if (isLiveVisitor && !isClosed && !isAssignMe) return "enter-join";
            //     if (!isLiveVisitor && isClosed) return "inactive";
            // } else {
            //     if (isClosed) return "enter-create";
            //     else if (!isAssignMe) return "enter-join";
            // }
            return null;
        },
    },
    data() {
        return {
            message: "",
            mailSubject: "",
            smsInput: {},
            aiChangeText: null,
            lastMessageUpdateTS: null,
            conversationMode: "chat",
            isSendBtnActive: true,
            speechRecognitionActive: false,
            bannedWordDetected: false,
            superMenuState: {
                enabled: false,
            },
            draftMessageTimers: {},
            cancelTokens: [],
            visitorTyping: null,
        };
    },
    mounted() {
        const readyContent = systemPubSub.stream
            .pipe(filter((x) => x.type === "ready_content_used"))
            .subscribe((data) => {
                const { content, contact } = data.payload || {};
                if (contact._id !== this.contact._id) return;
                this.$refs.editor.setContent(content);
            });
        const focusContent = systemPubSub.stream.pipe(filter((x) => x.type === "focus_content")).subscribe(() => {
            this.focusEditor();
        });
        this.cancelTokens.push(() => {
            readyContent.unsubscribe();
            focusContent.unsubscribe();
        });
        this.bindDraftMessage();
    },
    methods: {
        ...mapActions({
            addPendingMessage: "chat/addPendingMessage",
            removeDraftMessage: "chat/removeDraftMessage",
            createMailAddress: "mail/createMailAddress",
            addDraftMessage: "chat/addDraftMessage",
            unpinReplyToMessage: "chat/unpinReplyToMessage",
        }),
        usedSlashCommand(response) {
            if (response.command === "pm" && response.value.length > 0) {
                const replyToMessage = this.$_.get(
                    this.replyToMessages,
                    `${this.contact.lastConversation._id}.raw`,
                    null
                );
                sendSocketMessage("user private-message", {
                    conversationId: this.contact.lastConversation._id,
                    userId: this.user._id,
                    username: this.user.name,
                    message: response.value,
                    siteId: this.site._id,
                    replyToMessage,
                });
                this.unpinReplyToMessage(this.contact.lastConversation._id);
            }
        },
        focusEditor() {
            this.$refs.editor.focus();
        },
        blurEditor() {
            this.$refs.editor.blur();
        },
        generateChatSuggestions() {
            const shortcut = getRandomShortcut();
            return `💡 ${shortcut[this.$i18n.locale] || shortcut.tr}`;
        },
        moveMessageAreaScrollToEnd() {
            const scMessageList = document.querySelector("#chat-messages > .sc-message-list");
            if (scMessageList) {
                scMessageList.scroll(scMessageList.scrollWidth, scMessageList.scrollHeight);
            }
        },
        bindDraftMessage(conversationId) {
            const draftMessage = this.draftMessages[conversationId || this.contact.lastConversation._id];
            this.$refs.editor.setContent(draftMessage || "");
        },
        modeChanged(mode) {
            this.conversationMode = mode;
        },
        setMessage(val) {
            this.$refs.editor.setContent(val);
        },
        submitForm(message) {
            if (this.conversationMode === "chat") {
                this.sendMessage(message);
            } else if (this.conversationMode === "email") {
                this.sendMail(message);
            } else if (this.conversationMode === "sms") {
                this.sendSMS({
                    text: message,
                    ...this.smsInput,
                });
            }
        },

        async sendMessage(message) {
            if (!this.isInputAllowed()) {
                const message = {
                    en: "You are not allowed to send message. Please check whatsapp integration",
                    tr: "Bu mesajı gönderemezseniz whatsapp entegrasyonunu kontrol edin",
                    ru: "Вы не можете отправить сообщение. Пожалуйста, проверьте интеграцию whatsapp",
                    de: "Wenn Sie diese Nachricht nicht senden können, überprüfen Sie die WhatsApp-Integration",
                    fr: "Si vous ne pouvez pas envoyer ce message, vérifiez l'intégration WhatsApp",
                    es: "Si no puede enviar este mensaje, verifique la integración de WhatsApp",
                    ar: "إذا لم تتمكن من إرسال هذه الرسالة ، فتحقق من تكامل whatsapp",
                    it: "Se non puoi inviare questo messaggio controlla l'integrazione di whatsapp",
                };
                this.$toasted.error(message[this.$i18n.locale], {
                    duration: 2000,
                    position: "bottom-center",
                });
                return;
            }
            const replyToMessage = this.$_.get(this.replyToMessages, `${this.contact.lastConversation._id}.raw`, null);
            message = (typeof message === "string" && message) || this.message;

            if (message.trim().length) {
                let clearedMessage = message
                    .trim()
                    .replace(/↵/g, "\n")
                    .replace(/^(\s+<br( \/)?>)*|(<br( \/)?>\s)*$/gm, "")
                    .replace(/<a (.*)>/g, '<a target="_blank" $1>');

                for (const m of ["@name", "@email"]) {
                    let val = m === "@name" ? this.contact.fullname : this.contact.email;
                    clearedMessage = clearedMessage.replace(new RegExp(m, "g"), val);
                }

                let payload = {
                    conversationId: this.contact.lastConversation._id,
                    visitorId: this.contact._id,
                    userId: this.user._id,
                    username: this.user.name,
                    message: clearedMessage,
                    siteId: this.site._id,
                    channelId: _.get(this.contact, "lastConversation.channel", null),
                    whatsappNumber: _.get(this.contact, "lastConversation.whatsappNumber", null),
                    instagramUserId: _.get(this.contact, "lastConversation.instagramUserId", null),
                    messengerUserId: _.get(this.contact, "lastConversation.messengerUserId", null),
                    telegramUserId: _.get(this.contact, "lastConversation.telegramUserId", null),
                    uniqMessageId: generateUUID(),
                    replyToMessage,
                };
                sendSocketMessage("user message", payload);
                this.message = "";
                this.addPendingMessage(payload);
                this.unpinReplyToMessage(this.contact.lastConversation._id);
                setTimeout(() => {
                    this.removeDraftMessage(this.contact.lastConversation._id);
                }, 100);
            }

            this.moveMessageAreaScrollToEnd();
        },
        async sendMail(message) {
            if (message.length === 0) {
                this.$message.error("Mesaj alanı boş geçilemez");
            } else if (this.mailSubject.length === 0) {
                this.$message.error("Subject alanı boş geçilemez");
            } else if (!this.contact.email || this.contact.email.length === 0) {
                this.$message.error("Kullanıcı mail adresi bulunmamaktadır");
            } else {
                const conversation = this.contact.lastConversation;
                this.$message.warning("Mail gönderiliyor...");
                this.createMailAddress({
                    siteId: this.site._id,
                    mail: this.contact.email,
                    type: "visitor",
                    visitorId: this.contact._id,
                });
                const userMailAddress = this.allMailAddresses.filter(
                    (x) => !!x.user && x.user._id === this.user._id && x.idDefault === true
                );
                const regex = /(<([^>]+)>)/gi;
                const textMessage = message.replace(regex, "");
                const data = {
                    ref_visitor_id: this.contact._id,
                    to_list: [this.contact.email],
                    sender: userMailAddress[0].mail,
                    ref_user_ids: [this.user._id],
                    senderName: this.user.name,
                    body_html: message,
                    body_text: textMessage,
                    subject: this.mailSubject,
                    attachments: [],
                    fileNames: [],
                    files: [],
                };
                const requestId = generateUUID();
                const emailPromise = this.waitMail(requestId);
                this.isSendBtnActive = false;
                sendSocketMessage("email send", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    requestId,
                    data,
                });
                const email = await emailPromise;
                this.isSendBtnActive = true;
                let payload = {
                    conversationId: conversation._id,
                    mail: email,
                    username: this.user.name,
                    userId: this.user._id,
                    siteId: this.site._id,
                    messageType: "user-email",
                    message: "",
                    channelId: _.get(conversation, "channel", null),
                    whatsappNumber: _.get(conversation, "whatsappNumber", null),
                    instagramUserId: _.get(conversation, "instagramUserId", null),
                    messengerUserId: _.get(conversation, "messengerUserId", null),
                    telegramUserId: _.get(conversation, "telegramUserId", null),
                    uniqMessageId: generateUUID(),
                };
                this.addPendingMessage(payload);
                sendSocketMessage("user mail", payload);
                this.mailSubject = "";
            }
            this.moveMessageAreaScrollToEnd();
        },
        async sendSMS({ channelId, senderId, countryCode, number }) {
            if (!channelId || !senderId || !number) {
                this.$message.error("Lütfen gerekli yerleri doldurunuz."); // TODO: translate
                return;
            }

            const payload = {
                siteId: this.site._id,
                channelId,
                channelType: "sms-postacell",
                number: countryCode + number,
                message: this.message,
                senderId,
                title: null,
                customMessageId: generateUUID(),
            };
            sendSocketMessage("sms send-single", payload);
            const conversation = this.contact.lastConversation;
            let data = {
                conversationId: conversation._id,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                messageType: "user-sms",
                message: this.message,
                channelId: channelId,
                whatsappNumber: _.get(conversation, "whatsappNumber", null),
                instagramUserId: _.get(conversation, "instagramUserId", null),
                messengerUserId: _.get(conversation, "messengerUserId", null),
                telegramUserId: _.get(conversation, "telegramUserId", null),
                uniqMessageId: generateUUID(),
            };
            this.addPendingMessage(data);
            sendSocketMessage("user sms", data);
            this.moveMessageAreaScrollToEnd();
        },
        changeWithAI(payload) {
            this.$emit(
                "ai-copilot",
                payload && {
                    position: { from: payload.from, to: payload.to },
                    message: payload.text,
                }
            );
        },
        isInputAllowed() {
            const isWhatsappChat = !!this.contact.lastConversation.whatsappNumber;
            if (isWhatsappChat) {
                return true || this.isWhatsappHealthy;
            }
            return true;
        },
        updateSuperMenuState(payload) {
            this.superMenuState = payload;
        },
        waitMail(requestId) {
            return socketMessageStream
                .pipe(
                    first((x) => x.cmd === "email_send_successful" && x.data.requestId === requestId),
                    map((x) => x.data.email)
                )
                .toPromise();
        },
        sendTypingInfo: _.debounce(function () {
            if (this.message.length) {
                if (!!this.contact.lastConversation)
                    sendSocketMessage("user typing message", {
                        conversationId: this.contact.lastConversation._id,
                        userId: this.user._id,
                        username: this.user.name,
                        message: "rep-typing",
                        siteId: this.site._id,
                        channelId: _.get(this.contact, "lastConversation.channel", null),
                        whatsappNumber: _.get(this.contact, "lastConversation.whatsappNumber", null),
                        instagramUserId: _.get(this.contact, "lastConversation.instagramUserId", null),
                        messengerUserId: _.get(this.contact, "lastConversation.messengerUserId", null),
                        telegramUserId: _.get(this.contact, "lastConversation.telegramUserId", null),
                    });
            }
        }, 500),
        addDraftMessageDelayed(conversationId, message, interval = 1000) {
            let timerId = this.draftMessageTimers[conversationId];
            if (timerId) clearTimeout(timerId);
            timerId = setTimeout(() => {
                this.addDraftMessage({
                    conversationId,
                    message,
                });
            }, interval);
            this.cancelTokens.push(() => clearTimeout(timerId));
            if (timerId) this.draftMessageTimers[conversationId] = timerId;
        },
        sendLocation(location) {
            sendSocketMessage("user location message", {
                siteId: this.site._id,
                userId: this.user._id,
                username: this.user.name,
                conversationId: this.contact.lastConversation._id,
                location,
                whatsappNumber: _.get(this.contact.lastConversation, "whatsappNumber"),
                telegramUserId: _.get(this.contact.lastConversation, "telegramUserId"),
                channelId: _.get(this.contact.lastConversation, "channel"),
            });
        },
        async sendVoiceMessage(blob) {
            const replyToMessage = this.$_.get(this.replyToMessages, `${this.contact.lastConversation._id}.raw`, null);
            let path = `${this.site._id}/voice/${this.contact.lastConversation._id}`;
            const mimeType = "audio/mpeg";
            const ext = "mp3";
            let { data } = await uploadAsset({
                path,
                mime: mimeType,
                ext,
                platform: this.contact.lastConversation.ref,
            });
            await axios({
                method: data.method,
                url: data.url,
                data: blob,
                headers: { "Content-Type": mimeType },
            });
            sendSocketMessage("user media message", {
                conversationId: this.contact.lastConversation._id,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                asset: data.asset,
                channelId: _.get(this.contact, "lastConversation.channel", null),
                whatsappNumber: _.get(this.contact, "lastConversation.whatsappNumber", null),
                instagramUserId: _.get(this.contact, "lastConversation.instagramUserId", null),
                messengerUserId: _.get(this.contact, "lastConversation.messengerUserId", null),
                telegramUserId: _.get(this.contact, "lastConversation.telegramUserId", null),
                replyToMessage,
            });
            this.unpinReplyToMessage(this.contact.lastConversation._id);
            log("sendVoiceMessage", { blob, data });
        },
    },
    watch: {
        message(newValue) {
            if (newValue.includes("<span>")) {
                const regex = /<span>|<\/span>/gi;
                const message = newValue.replace(regex, "");
                this.$refs.editor.setContent(message);
            }
            if (this.settings.agentTyping) {
                this.sendTypingInfo();
            }
            if (this.contact.lastConversation) {
                this.addDraftMessageDelayed(this.contact.lastConversation._id, newValue);
            }
        },
        contact: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal && newVal?._id !== oldVal?._id) {
                    this.bindDraftMessage(newVal.lastConversation._id);
                }
            },
        },
    },
};
</script>
<style lang="scss">
.message-area {
    border-top: 1px solid var(--border-neutral-default);
    position: relative;

    &-container {
        width: 100%;
        display: flex;
        flex-direction: column;
    }
}
</style>
